import { AddressVerificationDialog } from '@/components/forms/Address/AddressVerificationDialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { getAddressRegionOptions } from '@/lib/address-region-options'
import { Country } from '@/lib/enums/Country'
import { useAppForm } from '@/lib/form'
import { usePlacesAutocomplete } from '@/hooks/use-places-autocomplete'
import { buildLimitedOrderDeliveryAddressFields } from '@/lib/order-address-limits'
import { ORDER_FIELD_MAX_LENGTHS } from '@/lib/order-field-limits'
import {
  applySuggestedDeliveryAddress,
  buildDeliveryAddressSearchString,
  clearDeliveryAddressVerification,
  createEmptyDeliveryAddress,
  createManuallyVerifiedAddress,
  isSuggestedAddressUsable,
  suggestedAddressMatchesTypedCore,
} from '@/components/forms/Orders/fulfillment-destination.helpers'
import dayjs from '@/lib/dayjs'
import { X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

export function buildManualDeliveryDefaultValues(stop, defaultDeliveryDate, defaultTimezone) {
  const deliveryDate =
    stop && Object.hasOwn(stop, 'delivery_date')
      ? (stop.delivery_date ?? '')
      : (defaultDeliveryDate ??
        (defaultTimezone ? dayjs().tz(defaultTimezone) : dayjs()).format('YYYY-MM-DD'))

  return {
    recipient_name: stop?.recipient_name ?? '',
    recipient_phone: stop?.recipient_phone ?? '',
    recipient_email: stop?.recipient_email ?? '',
    delivery_date: deliveryDate,
    special_instructions: stop?.special_instructions ?? '',
    delivery_address: createEmptyDeliveryAddress(stop?.delivery_address),
  }
}

export function getManualDeliveryDateBounds(defaultTimezone) {
  const today = defaultTimezone ? dayjs().tz(defaultTimezone) : dayjs()

  return {
    minDate: today.subtract(30, 'day').format('YYYY-MM-DD'),
    maxDate: today.add(365, 'day').format('YYYY-MM-DD'),
  }
}

export function buildManualDeliverySavePayload(initialStop, value, deliveryAddress) {
  const routeStopId = initialStop?.route_stop_id ?? initialStop?.id

  return {
    ...(routeStopId ? { id: routeStopId } : {}),
    source_type: 'manual',
    recipient_name: String(value.recipient_name).trim(),
    recipient_phone: String(value.recipient_phone ?? '').trim() || null,
    recipient_email: String(value.recipient_email ?? '').trim() || null,
    delivery_date: value.delivery_date || null,
    special_instructions: String(value.special_instructions ?? '').trim() || null,
    delivery_address: deliveryAddress,
  }
}

export default function ManualDeliveryDialog({
  defaultDeliveryDate,
  defaultTimezone,
  initialStop,
  onOpenChange,
  onSave,
  open,
}) {
  const { geocodeAddress, geocodePlaceId } = usePlacesAutocomplete()
  const [verificationPrompt, setVerificationPrompt] = useState(null)
  const operationGenerationRef = useRef(0)
  const openRef = useRef(open)
  const deliveryDateBounds = getManualDeliveryDateBounds(defaultTimezone)

  const invalidatePendingOperations = () => {
    operationGenerationRef.current += 1
  }

  const beginOperation = () => {
    invalidatePendingOperations()
    return operationGenerationRef.current
  }

  const isCurrentOperation = (operationGeneration) =>
    openRef.current && operationGenerationRef.current === operationGeneration

  const closeDialog = () => {
    openRef.current = false
    invalidatePendingOperations()
    setVerificationPrompt(null)
    onOpenChange(false)
  }

  const handleOpenChange = (nextOpen) => {
    if (nextOpen) {
      onOpenChange(true)
      return
    }

    closeDialog()
  }

  const finish = (value, deliveryAddress, operationGeneration) => {
    if (!isCurrentOperation(operationGeneration)) return

    const didSave = onSave(buildManualDeliverySavePayload(initialStop, value, deliveryAddress))
    if (didSave === false) return

    closeDialog()
  }

  // Invalidate geocoding work when a controlled parent closes the dialog.
  useEffect(() => {
    openRef.current = open
    if (!open) operationGenerationRef.current += 1
  }, [open])

  // Prevent an outstanding geocode response from updating an unmounted dialog.
  useEffect(
    () => () => {
      operationGenerationRef.current += 1
    },
    []
  )

  const form = useAppForm({
    defaultValues: buildManualDeliveryDefaultValues(
      initialStop,
      defaultDeliveryDate,
      defaultTimezone
    ),
    validators: {
      onSubmitAsync: async ({ value }) => {
        const errors = {}
        const address = value.delivery_address

        if (!String(value.recipient_name ?? '').trim()) {
          errors.recipient_name = 'Recipient or stop name is required.'
        }
        if (!String(address?.line1 ?? '').trim()) {
          errors['delivery_address.line1'] = 'Address is required.'
        }
        if (!String(address?.city ?? '').trim()) {
          errors['delivery_address.city'] = 'City is required.'
        }
        if (!String(address?.state ?? '').trim()) {
          errors['delivery_address.state'] = 'State is required.'
        }
        if (!String(address?.postal_code ?? '').trim()) {
          errors['delivery_address.postal_code'] = 'Zip code is required.'
        }

        if (Object.keys(errors).length > 0) {
          return { fields: errors }
        }

        const operationGeneration = beginOperation()

        if (address.is_verified) {
          finish(value, address, operationGeneration)
          return
        }

        const suggested = await geocodeAddress(buildDeliveryAddressSearchString(address))

        if (!isCurrentOperation(operationGeneration)) return

        if (
          isSuggestedAddressUsable(suggested) &&
          suggestedAddressMatchesTypedCore(address, suggested)
        ) {
          finish(value, applySuggestedDeliveryAddress(address, suggested), operationGeneration)
          return
        }

        setVerificationPrompt({ operationGeneration, suggested, typedAddress: address, value })
      },
    },
  })

  const resetVerification = () => {
    invalidatePendingOperations()
    setVerificationPrompt(null)

    const clearedAddress = clearDeliveryAddressVerification(
      form.store.state.values.delivery_address
    )
    form.setFieldValue('delivery_address.postal_code_suffix', clearedAddress.postal_code_suffix)
    form.setFieldValue('delivery_address.google_place_id', clearedAddress.google_place_id)
    form.setFieldValue('delivery_address.lat', clearedAddress.lat)
    form.setFieldValue('delivery_address.lng', clearedAddress.lng)
    form.setFieldValue('delivery_address.is_verified', clearedAddress.is_verified)
    form.setFieldValue('delivery_address.is_rooftop', clearedAddress.is_rooftop)
  }

  const handlePlaceSelected = async (place) => {
    if (!place) return

    const operationGeneration = beginOperation()
    const address = {
      ...form.store.state.values.delivery_address,
      ...buildLimitedOrderDeliveryAddressFields(place),
      country: place.country || Country.UnitedStates.Value,
      google_place_id: place.placeId || null,
      lat: place.lat ?? null,
      lng: place.lng ?? null,
      is_verified: Boolean(place.placeId && place.lat != null && place.lng != null),
      is_rooftop: false,
    }

    if (place.placeId) {
      const geocoded = await geocodePlaceId(place.placeId)

      if (!isCurrentOperation(operationGeneration)) return

      address.is_rooftop = geocoded?.locationType === 'ROOFTOP' && geocoded?.partialMatch !== true
    }

    if (!isCurrentOperation(operationGeneration)) return

    form.setFieldValue('delivery_address', address)
  }

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent size="large" viewport="scroll" className="max-h-[90dvh]">
          <DialogHeader>
            <DialogTitle>{initialStop ? 'Edit custom stop' : 'Add custom stop'}</DialogTitle>
            <DialogDescription>
              Add a route stop without creating an order or customer record.
            </DialogDescription>
          </DialogHeader>

          <form.AppForm>
            <form.Form className="space-y-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <form.AppField name="recipient_name">
                  {(field) => (
                    <field.TextInput
                      label="Recipient or stop name"
                      placeholder="Front desk, Jane Smith, etc."
                      characterMaxLength={255}
                    />
                  )}
                </form.AppField>
                <form.AppField name="delivery_date">
                  {(field) => (
                    <div className="flex items-end gap-2">
                      <field.DatePicker
                        label="Delivery date (optional)"
                        placeholder="No delivery date"
                        className="w-full"
                        minDate={deliveryDateBounds.minDate}
                        maxDate={deliveryDateBounds.maxDate}
                      />
                      {field.state.value ? (
                        <Button
                          type="button"
                          variant="ghostMuted"
                          size="icon"
                          className="shrink-0"
                          aria-label="Clear delivery date"
                          onClick={() => field.handleChange('')}
                        >
                          <X className="size-4" />
                        </Button>
                      ) : null}
                    </div>
                  )}
                </form.AppField>
                <form.AppField name="recipient_phone">
                  {(field) => <field.PhoneInput label="Phone (optional)" />}
                </form.AppField>
                <form.AppField name="recipient_email">
                  {(field) => (
                    <field.EmailInput label="Email (optional)" placeholder="name@example.com" />
                  )}
                </form.AppField>
              </div>

              <div className="space-y-4 border-t pt-5">
                <form.AppField name="delivery_address.line1">
                  {(field) => (
                    <field.AddressSearch
                      label="Delivery address"
                      placeholder="Start typing an address..."
                      onChange={resetVerification}
                      onAddressSelected={handlePlaceSelected}
                      characterMaxLength={255}
                    />
                  )}
                </form.AppField>
                <form.AppField name="delivery_address.line2">
                  {(field) => (
                    <field.TextInput
                      label="Apartment, suite, etc. (optional)"
                      characterMaxLength={255}
                    />
                  )}
                </form.AppField>
                <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_8rem_9rem]">
                  <form.AppField name="delivery_address.city">
                    {(field) => (
                      <field.TextInput
                        label="City"
                        onChange={resetVerification}
                        characterMaxLength={255}
                      />
                    )}
                  </form.AppField>
                  <form.AppField name="delivery_address.state">
                    {(field) => (
                      <field.SelectInput
                        label="State"
                        options={getAddressRegionOptions(field.state.value)}
                        onValueChange={resetVerification}
                      />
                    )}
                  </form.AppField>
                  <form.AppField name="delivery_address.postal_code">
                    {(field) => (
                      <field.TextInput
                        label="Zip code"
                        onChange={resetVerification}
                        characterMaxLength={ORDER_FIELD_MAX_LENGTHS.address_postal_code}
                      />
                    )}
                  </form.AppField>
                </div>
              </div>

              <form.AppField name="special_instructions">
                {(field) => (
                  <field.TextareaInput
                    label="Driver note (optional)"
                    placeholder="Gate code, parking instructions, what product to deliver…"
                    rows={3}
                    characterMaxLength={1000}
                  />
                )}
              </form.AppField>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={closeDialog}>
                  Cancel
                </Button>
                <form.SubmitButton
                  className="w-auto"
                  disableWhenInvalid={false}
                  keepChildrenWhileLoading
                >
                  {initialStop ? 'Save changes' : 'Add delivery'}
                </form.SubmitButton>
              </DialogFooter>
            </form.Form>
          </form.AppForm>
        </DialogContent>
      </Dialog>

      <AddressVerificationDialog
        open={Boolean(verificationPrompt)}
        title="Confirm this custom stop address"
        typedAddress={verificationPrompt?.typedAddress}
        suggested={verificationPrompt?.suggested}
        isSuggestedUsable={isSuggestedAddressUsable(verificationPrompt?.suggested)}
        onChooseTyped={() => {
          if (verificationPrompt) {
            finish(
              verificationPrompt.value,
              createManuallyVerifiedAddress(verificationPrompt.typedAddress),
              verificationPrompt.operationGeneration
            )
          }
          setVerificationPrompt(null)
        }}
        onChooseSuggested={() => {
          if (verificationPrompt && isSuggestedAddressUsable(verificationPrompt.suggested)) {
            finish(
              verificationPrompt.value,
              applySuggestedDeliveryAddress(
                verificationPrompt.typedAddress,
                verificationPrompt.suggested
              ),
              verificationPrompt.operationGeneration
            )
          }
          setVerificationPrompt(null)
        }}
        onCancel={() => setVerificationPrompt(null)}
      />
    </>
  )
}
